import React, { useCallback } from 'react';
import { Paper, Icon, Button, TextField, makeStyles, Menu, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { IssueConditionValue, MonitorSeverity, Issue, RobotInfo } from '@moderepo/mode-apis';
import { usePropValue, inputStateSetterHelper, useModePanelStyle, useModeFormStyle, FontIcon, BaseEntityField, BaseInfoCompProps,
    BaseInfoCompActionsSet, BaseInfoCompSaveAction, useDropdown, InfoCompPanelHeader, ModeInfoCompSelectInputField, ModeInfoCompTextInputField }
    from '@moderepo/mode-app-components';
import { RCRobotsListingContainer, RobotsTableHiddenFields } from '../../containers/robot-cloud/RCRobotsListingContainer';


const useStyle = makeStyles((theme: Theme) => {
    return {
        robotsMenuTrigger: {
            '&.editable': {
                cursor   : 'pointer',
                '& input': {
                    cursor: 'pointer',
                },
            },
        },

        robotsMenu: {
            width                       : '100%',
            [theme.breakpoints.up('md')]: {
                width: 600,
            },
            padding: 0,
            '& ul' : {
                width  : '100%',
                padding: 0,
            },
        },

        robotsListWrapper: {
            width  : '100%',
            padding: 0,
        },

        robotsList: {
            width  : '100%',
            padding: 0,
        },

    };
}, { name: 'RCIssueInfo' });



/**
 * Robot select dropdown requires special props so we need to extends ComponentInputField
 */
interface RobotInputField <T> extends BaseEntityField<T> {
    readonly robotsListingContainerProps: {
        readonly projectId: number,
        readonly title: string,
        readonly itemsPerPage: number,
        readonly hiddenFields: RobotsTableHiddenFields,
    },
}



/**
 * This is the interface of the set of input fields' values that will be passed to the onClick handler when the 'saveUpdate' button is clicked.
 */
export interface RCIssueInfoUpdatableInputs {
    readonly robot?: RobotInfo | undefined;
    readonly severity?: MonitorSeverity | undefined;
    readonly description?: string | undefined;
    readonly ticketLink?: string | undefined;
}


/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action.
 */
export interface RCIssueInfoActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate: BaseInfoCompSaveAction<RCIssueInfoUpdatableInputs>
}


export interface RCIssueInfoProps extends BaseInfoCompProps {
    readonly issue?: Issue | undefined;

    readonly fields: {
        readonly id: BaseEntityField<number>;
        readonly deviceId?: BaseEntityField<number>;
        readonly robotId?: BaseEntityField<string>;
        readonly homeId?: BaseEntityField<number>;
        readonly robot?: RobotInputField<RobotInfo>;
        readonly monitorId: BaseEntityField<number>;
        readonly severity: BaseEntityField<MonitorSeverity>;
        readonly description: BaseEntityField<string>;
        readonly values: readonly BaseEntityField<IssueConditionValue>[];
        readonly ticketLink: BaseEntityField<string>;
        readonly createdBy: BaseEntityField<number>;
        readonly resolvedBy: BaseEntityField<number>;
        readonly creationTime: BaseEntityField<string>;
        readonly resolvedTime: BaseEntityField<string>;
        readonly resolveStatus: BaseEntityField<string>;
    }

    readonly actions?: RCIssueInfoActionsSet | undefined;

    readonly texts: {
        readonly createdByGroupTitle: string;
    }
}



/**
 * This is a DUMB component used for displaying issue info. This component does not have any logic. All it does it display
 * data based on the props provided by the container. All action done in this component will be dispatched back to the
 * container's handler.
 */
export const RCIssueInfo = (props: RCIssueInfoProps) => {
    const [robotInput, setRobotInput] = usePropValue<RobotInfo | undefined>(props.fields.robot?.value);
    const [issueSeverityInput, setIssueSeverityInput] = usePropValue<MonitorSeverity | undefined>(props.fields.severity.value);
    const [descriptionInput, setDescriptionInput] = usePropValue<string | undefined>(props.fields.description.value);
    const [ticketLinkInput, setTicketLinkInput] = usePropValue<string | undefined>(props.fields.ticketLink.value);

    const [robotIdMenuAnchorEl, isRobotIdMenuOpened, onRobotIdAnchorElClicked, onRobotIdMenuClosed] = useDropdown();


    /**
     * On input change handler for all the inputs. When an input change, call the onInputChangeHelper and pass it the setter functions
     * for all inputs. The helper function will call the setter function accordingly based on the input name.
     */
    const onInputChange = useCallback((inputName: string, value: any) => {
        inputStateSetterHelper(inputName, value, {
            robot      : setRobotInput,
            severity   : setIssueSeverityInput,
            description: setDescriptionInput,
            ticketLink : setTicketLinkInput,
        });
    }, [setRobotInput, setIssueSeverityInput, setDescriptionInput, setTicketLinkInput]);



    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();


    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {
        setRobotInput(props.fields.robot?.value);
        setIssueSeverityInput(props.fields.severity.value);
        setDescriptionInput(props.fields.description.value);
        setTicketLinkInput(props.fields.ticketLink.value);
    }, [props, setRobotInput, setIssueSeverityInput, setDescriptionInput, setTicketLinkInput]);



    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick({
                robot      : robotInput,
                severity   : issueSeverityInput,
                description: descriptionInput?.trim(),
                ticketLink : ticketLinkInput?.trim(),
            });
        }

        return false;
    }, [props, robotInput, issueSeverityInput, descriptionInput, ticketLinkInput]);



    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                <form
                    className={clsx(formClasses.root, props.formProps?.className)}
                    noValidate
                    autoComplete="on"
                    method="POST"
                    onSubmit={onFormSubmit}
                >
                    <div className={formClasses.formInputGroup}>
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.id}
                            fieldName="id"
                            inputValue={props.fields.id.displayValue ?? props.fields.id.value}
                            onInputChange={onInputChange}
                        />
                        {props.fields.deviceId && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={props.fields.deviceId}
                                fieldName="deviceId"
                                inputValue={props.fields.deviceId.displayValue ?? props.fields.deviceId.value}
                                onInputChange={onInputChange}
                            />
                        )}
                        {/* For displaying existing Issue where we know the robotId and homeId and robotId is not editable */}
                        {props.fields.robotId && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={props.fields.robotId}
                                fieldName="robotId"
                                inputValue={props.fields.robotId.displayValue ?? props.fields.robotId.value}
                                onInputChange={onInputChange}
                            />
                        )}
                        {props.fields.homeId && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={props.fields.homeId}
                                fieldName="homeId"
                                inputValue={props.fields.homeId.displayValue ?? props.fields.homeId.value}
                                onInputChange={onInputChange}
                            />
                        )}

                        {/* For displaying NEW Issue where we need the user to select a robot */}
                        {props.fields.robot && !props.fields.robot.hidden && (
                            <>
                                <TextField
                                    className={clsx(formClasses.formInputField)}
                                    required
                                    variant="standard"
                                    type="text"
                                    fullWidth
                                    label={props.fields.robot.label}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    InputProps={{
                                        readOnly    : true,
                                        endAdornment: (
                                            <Icon>arrow_drop_down</Icon>
                                        ),
                                        className: clsx(classes.robotsMenuTrigger, props.fields.robot.editable && 'editable'),
                                    }}
                                    error={Boolean(props.fields.robot.error)}
                                    helperText={props.fields.robot.error ? props.fields.robot.error : ''}
                                    value={robotInput?.id || ''}
                                    onClick={props.fields.robot.editable ? onRobotIdAnchorElClicked : undefined}
                                />

                                <Menu
                                    getContentAnchorEl={null}
                                    anchorEl={robotIdMenuAnchorEl}
                                    anchorOrigin={{
                                        vertical  : 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical  : 'top',
                                        horizontal: 'right',
                                    }}
                                    PaperProps={{
                                        className: classes.robotsMenu,
                                    }}
                                    keepMounted
                                    open={isRobotIdMenuOpened}
                                    onClose={onRobotIdMenuClosed}
                                >
                                    <span className={classes.robotsListWrapper}>
                                        <RCRobotsListingContainer
                                            {...props.fields.robot.robotsListingContainerProps}
                                            gatewayDependentRobotsOnly={false}
                                            canCreateRobots={false}
                                            canLinkRobots={false}
                                            onRobotSelected={(robot: RobotInfo) => {
                                                if (props.onFormInputChange) {
                                                    props.onFormInputChange('robot', robot);
                                                }
                                                onInputChange('robot', robot);
                                                onRobotIdMenuClosed();
                                            }}
                                        />
                                    </span>
                                </Menu>
                            </>
                        )}

                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            multiline
                            compProps={props}
                            inputProps={props.fields.description}
                            fieldName="description"
                            inputValue={props.fields.description.editable
                                ? descriptionInput
                                : props.fields.description.displayValue ?? props.fields.description.value}
                            onInputChange={onInputChange}
                        />
                        <ModeInfoCompSelectInputField
                            className={formClasses.formInputField}
                            compProps={props}
                            inputProps={props.fields.severity}
                            fieldName="severity"
                            inputValue={props.fields.severity.editable
                                ? issueSeverityInput
                                : props.fields.severity.displayValue ?? props.fields.severity.value}
                            onInputChange={onInputChange}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.resolveStatus}
                            fieldName="resolveStatus"
                            inputValue={props.fields.resolveStatus.displayValue ?? props.fields.resolveStatus.value}
                            onInputChange={onInputChange}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.ticketLink}
                            fieldName="ticketLink"
                            inputValue={props.fields.ticketLink.editable
                                ? ticketLinkInput : props.fields.ticketLink.displayValue ?? props.fields.ticketLink.value}
                            onInputChange={onInputChange}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.resolvedBy}
                            fieldName="resolvedBy"
                            inputValue={props.fields.resolvedBy.displayValue ?? props.fields.resolvedBy.value}
                            onInputChange={onInputChange}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.resolvedTime}
                            fieldName="resolvedTime"
                            inputValue={props.fields.resolvedTime.displayValue ?? props.fields.resolvedTime.value}
                            onInputChange={onInputChange}
                        />

                        {!props.isEditing && (
                            <div className={formClasses.formInputSection}>
                                <div className={formClasses.formInputSectionTitle}>
                                    {props.texts.createdByGroupTitle}
                                </div>
                                <div className={formClasses.formInputSectionContent}>
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        compProps={props}
                                        inputProps={props.fields.createdBy}
                                        fieldName="createdBy"
                                        inputValue={props.fields.createdBy.displayValue ?? props.fields.createdBy.value}
                                        onInputChange={onInputChange}
                                    />
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        compProps={props}
                                        inputProps={props.fields.monitorId}
                                        fieldName="monitorId"
                                        inputValue={props.fields.monitorId.displayValue ?? props.fields.monitorId.value}
                                        onInputChange={onInputChange}
                                    />
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        compProps={props}
                                        inputProps={props.fields.creationTime}
                                        fieldName="creationTime"
                                        inputValue={props.fields.creationTime.displayValue ?? props.fields.creationTime.value}
                                        onInputChange={onInputChange}
                                    />
                                    {props.fields.values.map((value: BaseEntityField<IssueConditionValue>) => {
                                        return (
                                            <ModeInfoCompTextInputField
                                                key={value.label}
                                                className={formClasses.formInputField}
                                                inputType="text"
                                                compProps={props}
                                                inputProps={value}
                                                fieldName="conditionValue"
                                                inputValue={value.displayValue ?? value.value}
                                                onInputChange={onInputChange}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                    {props.isEditing && props.actions && (
                        <div className={clsx(formClasses.formActionContainer)}>
                            <div />
                            <div>
                                {props.actions?.cancelEdit && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        startIcon={props.actions.cancelEdit.icon
                                            ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                        onClick={() => {
                                            reverseFormInput();
                                            if (props.actions?.cancelEdit) {
                                                props.actions.cancelEdit.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.cancelEdit.label}
                                    </Button>
                                )}
                                {props.actions?.saveUpdate && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        startIcon={props.actions.saveUpdate.icon
                                            ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                    >
                                        {props.actions.saveUpdate.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </div>

        </Paper>
    );

};
